import React, { useEffect, useMemo, useState } from "react";
import { uploadEHCP, extractEHCPSectionE, runEhcpV2Synthesis } from "../lib/api";
import toast from "react-hot-toast";
import { Icons } from "../lib/Icons";


export default function EHCPUpload({ studentId, onProcessingComplete, onEhcpStatusChange, onBusyChange }) {
  const [file, setFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [synthesizing, setSynthesizing] = useState(false);
  const [message, setMessage] = useState("");
  const [latestDocId, setLatestDocId] = useState(null);
  const [dropActive, setDropActive] = useState(false);
  const [summary, setSummary] = useState(null);

  const busy = uploading || extracting || synthesizing;

  useEffect(() => {
    onBusyChange?.(busy);
  }, [busy, onBusyChange]);

  const progressLabel = useMemo(() => {
    if (uploading) return `Uploading PDF... ${Math.round(uploadProgress)}%`;
    if (extracting) return "Extracting Section E outcomes...";
    if (synthesizing) return "Synthesizing whole-child profile...";
    return "";
  }, [uploading, extracting, synthesizing, uploadProgress]);

  const setPdfFile = (candidate) => {
    if (!candidate) return;
    if (!candidate.name?.toLowerCase?.().endsWith(".pdf")) {
      toast.error("Please upload a PDF file");
      return;
    }
    setFile(candidate);
    setMessage(`Selected: ${candidate.name}`);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDropActive(false);
    setPdfFile(e.dataTransfer?.files?.[0]);
  };

  const handleUpload = async () => {
    if (!file || !studentId) {
      setMessage("Please select a file and ensure a valid student ID is provided.");
      return;
    }

    try {
      setUploading(true);
      setMessage("Uploading EHCP document...");
      setUploadProgress(50);

      // Upload to signed URL via callable
      const result = await uploadEHCP(studentId, file);
      
      setUploadProgress(100);
      setMessage("Upload successful. Extracting Section E outcomes...");
      setLatestDocId(result.docId);
      toast.success("EHCP uploaded successfully");

      // Keep v1 Section E extraction for compatibility with current EHCP views.
      setExtracting(true);
      let extracted = null;
      try {
        extracted = await extractEHCPSectionE(result.docId);
        setMessage("Section E extracted. Running whole-child synthesis...");
        toast.success("Section E outcomes extracted");
      } catch (extractError) {
        console.error("Error extracting Section E:", extractError);
        setMessage("Upload succeeded, but Section E extraction failed. You can retry from the student profile.");
        toast.error("Failed to extract Section E");
      } finally {
        setExtracting(false);
      }

      // Run v2 extraction and profile synthesis for command-center readiness.
      setSynthesizing(true);
      try {
        const v2 = await runEhcpV2Synthesis({
          studentId,
          storagePath: result.storagePath,
          fileSizeBytes: file.size,
          mimeType: file.type || "application/pdf",
        });
        setSummary({
          jobId: v2?.job?.jobId || null,
          hasSectionE: Boolean(v2?.extraction?.sectionE),
          hasSectionF: Boolean(v2?.extraction?.sectionF),
          tier: v2?.profile?.tierDecision?.tier || null,
        });
        setMessage("EHCP processed and whole-child profile updated.");
        toast.success("Whole-child profile updated");
      } catch (v2Error) {
        console.error("Error running v2 synthesis:", v2Error);
        const detail = v2Error?.message || "";
        const openAiHint = /openai|connection error/i.test(detail)
          ? " The AI extraction service could not be reached. Check that OPENAI_API_KEY is configured on Cloud Functions, then retry."
          : "";
        setMessage(`EHCP upload succeeded, but AI extraction did not complete.${openAiHint}`);
        toast.error("AI extraction failed. You can retry from Upload & Process");
      } finally {
        setSynthesizing(false);
      }

      if (onProcessingComplete) {
        onProcessingComplete(extracted || null);
      }
      onEhcpStatusChange?.();

    } catch (error) {
      console.error("Error uploading EHCP:", error);
      setMessage(`Upload failed: ${error.message}`);
      toast.error("EHCP upload failed");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="border p-4 rounded-lg bg-gray-50">
      <h3 className="text-lg font-semibold mb-2 flex items-center"><Icons.Document className="w-5 h-5 mr-2" /> Upload EHCP Document</h3>
      <p className="mb-3 text-sm text-gray-600">
        Drag and drop a PDF or choose a file. The system extracts Section E/F and updates the child profile.
      </p>
      <div
        onDragOver={(e) => {
          e.preventDefault();
          if (!busy) setDropActive(true);
        }}
        onDragLeave={() => setDropActive(false)}
        onDrop={busy ? undefined : handleDrop}
        className={`mb-3 rounded-lg border-2 border-dashed p-4 transition-colors ${
          dropActive ? "border-blue-500 bg-blue-50" : "border-gray-300 bg-white"
        }`}
      >
        <input
          type="file"
          accept="application/pdf"
          disabled={busy}
          onChange={(e) => setPdfFile(e.target.files?.[0])}
          className="block w-full text-sm text-gray-700"
        />
      </div>
      <button
        className="mt-2 px-4 py-1 bg-blue-600 text-white rounded hover:bg-blue-700"
        onClick={handleUpload}
        disabled={!file || busy}
      >
        {busy ? "Processing..." : "Upload and Process"}
      </button>
      {progressLabel && <p className="mt-2 text-sm text-gray-600">{progressLabel}</p>}
      {message && (
        <p className={`mt-2 text-sm ${
          message.includes("Error") || message.includes("failed") 
            ? "text-red-700 bg-red-50 p-2 rounded border border-red-200" 
            : "text-gray-700"
        }`}>
          {message}
        </p>
      )}
      {latestDocId && (
        <p className="mt-2 text-xs text-gray-500">EHCP document reference: {latestDocId}</p>
      )}
      {summary && (
        <div className="mt-3 rounded-lg border border-gray-200 bg-white p-3 text-sm">
          <p className="font-medium text-gray-800">Processing summary</p>
          <ul className="mt-1 list-disc pl-5 text-gray-600">
            <li>Document job: {summary.jobId || "n/a"}</li>
            <li>Section E extracted: {summary.hasSectionE ? "Yes" : "No"}</li>
            <li>Section F extracted: {summary.hasSectionF ? "Yes" : "No"}</li>
            <li>Assigned support tier: {summary.tier || "Not assigned"}</li>
          </ul>
        </div>
      )}
    </div>
  );
}

